import { FormattedValue } from '@e2m-customer-portal/ui';
import { Tooltip, Typography, useTheme } from '@mui/material';
import { TypographyTypeMap } from '@mui/material/Typography/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { InfoIcon } from '../../../assets/icons';
import { RevenueItem } from '../../../generated/graphql';
import { cn } from '../../../utils/class-names';
import { ReactNode } from 'react';

const AMOUNT_UNIT: Record<string, string | undefined> = {
  RE03: '%',
  RQ03: '%',
  RE28: '%',
  RQ04: '%',
};

const SUM_UNIT: Record<string, string | undefined> = {
  RE29: '%',
};

const FRACTION_DIGITS: Record<string, number | undefined> = {
  RE03: 2,
  RQ03: 2,
  RE28: 2,
  RQ04: 2,
};

const PREFIX: Record<string, string | undefined> = {
  RE31: '-',
  RQ08: '-',
};

export const RevenueGridItem = ({
  item,
  label,
  contextInfo,
  variant,
  indentLevel = '0.5',
  icon,
  onClick,
  className,
  isGrey,
}: {
  label: string;
  item?: RevenueItem;
  contextInfo?: string | ReactNode;
  variant?: TypographyTypeMap['props']['variant'];
  indentLevel?: string;
  icon?: React.JSX.Element;
  onClick?: () => void;
  className?: string;
  isGrey?: boolean;
}): React.JSX.Element => {
  const [t] = useTranslation();
  const theme = useTheme();
  const isScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isLp = (id: string): boolean => ['RE17', 'RE18', 'RE20', 'RE22', 'RE24'].includes(id);
  const WHITELISTED_IDS = ['RE17', 'RE18', 'RE19', 'RE20', 'RE21', 'RE22', 'RE23', 'RE24', 'RE25'];
  const EMPHASIZED_IDS = new Set(['RE35', 'RE36', 'RQ06', 'RE26', 'RE32']);

  const hasValues = (item: RevenueItem): boolean => item.amount != null || item.sum != null;
  return (
    <div
      className={clsx(
        'col-span-full grid border-collapse gap-0 sm:grid-cols-5',
        {
          'sm:bg-grey-50': isGrey,
        },
        // following code create a double border to highlight some table section, it is not pretty but necessary
        { 'border-solid border-0 border-t-[1.5px] border-t-blue-grey-400 box-border': item?.key === 'RE26' },
        { 'border-solid border-0 border-b-[1.5px] border-t-blue-grey-400 box-border': item?.key === 'RE36' },
      )}
    >
      <button
        type="button"
        onClick={onClick}
        className={clsx(
          'border-divider bg-grey-50 box-border flex border-collapse items-center justify-between border-0 border-b border-r border-solid py-2 text-left sm:col-span-2',
          {
            'sm:bg-white': !isGrey,
          },
          className,
        )}
      >
        <div className="flex items-center justify-start">
          <Typography
            variant={variant ?? 'body2'}
            style={{ paddingLeft: isScreen ? `calc(1rem + ${indentLevel}rem)` : '0.5rem' }}
            className={cn({
              'text-sm': !isScreen,
              'text-grey-300': item && !hasValues(item) && WHITELISTED_IDS.includes(item.key),
              'font-semibold': item && EMPHASIZED_IDS.has(item.key),
            })}
          >
            {label}
          </Typography>
          {contextInfo && (
            <div className="ml-2 pt-1">
              <Tooltip title={contextInfo} placement={isScreen ? 'right-start' : 'top'}>
                <InfoIcon className="w-4 sm:text-green-200" />
              </Tooltip>
            </div>
          )}
        </div>
        <div className="pr-0 lg:pr-7">{icon}</div>
      </button>

      {item ? (
        <>
          <div className="border-divider col-span-1 box-border flex border-collapse items-center justify-between border-0 border-b border-r  border-solid px-2 text-right sm:justify-end lg:px-8">
            <div className="py-2 pl-2 text-xs sm:hidden">{t('REVENUE_REPORT_TABLE_HEADER_AMOUNT')}</div>

            <FormattedValue
              className="text-xs lg:text-sm"
              variant="body2"
              value={item?.amount}
              unit={AMOUNT_UNIT[item.key] ?? 'MWh'}
              fractionDigits={FRACTION_DIGITS[item.key] ?? 6}
              prefix={item?.amount != null && item?.amount > 0 && PREFIX[item.key]}
            />
          </div>

          <div className="border-divider col-span-1 box-border flex border-collapse items-center justify-between border-0 border-b border-r border-solid px-2 text-right sm:justify-end lg:px-8">
            <div className="py-2 pl-2 text-xs sm:hidden">{t('REVENUE_REPORT_TABLE_HEADER_PRICE')}</div>

            <FormattedValue
              className="text-xs lg:text-sm"
              variant="body2"
              value={item?.price}
              unit={isLp(item.key) ? '€/MW' : '€/MWh'}
              fractionDigits={2}
            />
          </div>

          <div className="border-divider col-span-1 box-border flex border-collapse items-center justify-between border-0 border-b border-solid px-2 text-right sm:justify-end sm:gap-4">
            <div className="py-2 pl-2 text-xs sm:hidden">{t('REVENUE_REPORT_TABLE_HEADER_RESULT')} </div>

            <div className="flex items-center pr-6">
              <FormattedValue
                variant="body2"
                value={item?.sum}
                unit={SUM_UNIT[item.key] ?? '€'}
                fractionDigits={2}
                className={cn('text-xs lg:text-sm', {
                  'font-semibold': item && EMPHASIZED_IDS.has(item.key),
                })}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="border-divider box-border flex border-collapse items-center justify-end border-0 border-b border-solid px-2 sm:col-span-5 sm:col-start-3" />
      )}
    </div>
  );
};
